import { addDays, daysBetween, firstOfMonth, isValidDay, lastDayOfMonth, previousMonth } from './dates';

/**
 * Day ranges as the API takes them (inclusive Europe/Skopje days). Limits from the M4 contract:
 * payments / summary / export at most 92 days, reconciliation at most 31; without the web panel
 * only today and the previous 7 days are free (a day D is free when D >= today − 7).
 */
export const PAYMENTS_MAX_DAYS = 92;
export const RECONCILIATION_MAX_DAYS = 31;
export const FREE_HISTORY_DAYS = 7;

export interface DayRangeValue {
  from: string;
  to: string;
}

export type RangeProblem = 'invalid' | 'order' | 'tooLong' | 'future';

export function checkRange(range: DayRangeValue, maxDays: number, today: string): RangeProblem | null {
  if (!isValidDay(range.from) || !isValidDay(range.to)) return 'invalid';
  if (range.from > range.to) return 'order';
  if (daysBetween(range.from, range.to) + 1 > maxDays) return 'tooLong';
  if (range.to > today) return 'future';
  return null;
}

/** The range reaches further back than the free history (needs the web panel plan). */
export function needsPanel(range: DayRangeValue, today: string): boolean {
  return range.from < addDays(today, -FREE_HISTORY_DAYS);
}

export type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month' | 'lastMonth';

export function presetRange(preset: PeriodPreset, today: string): DayRangeValue {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case 'week':
      return { from: addDays(today, -6), to: today };
    case 'month':
      return { from: firstOfMonth(today), to: today };
    case 'lastMonth': {
      const m = previousMonth(today.slice(0, 7));
      return { from: `${m}-01`, to: lastDayOfMonth(m) };
    }
  }
}

/** The preset a range corresponds to (for highlighting), if any. */
export function presetOf(
  range: DayRangeValue,
  today: string,
  presets: readonly PeriodPreset[],
): PeriodPreset | null {
  for (const p of presets) {
    const r = presetRange(p, today);
    if (r.from === range.from && r.to === range.to) return p;
  }
  return null;
}

/** A one-day range goes to the API as ?date= (M3 form); longer ones as ?from=&to=. */
export function toApiRange(range: DayRangeValue): { date: string } | { from: string; to: string } {
  return range.from === range.to ? { date: range.from } : { from: range.from, to: range.to };
}
